import React, { useEffect, useState, useRef } from "react";
import { fetchNodeById, fetchAnswersForNode, supabase } from "../api";
import { v4 as uuidv4 } from "uuid";
import QuizNode from "./QuizNode";
import { smoothScrollTo } from "../utils/scrollHelpers";
import { getLandingContext } from "../utils/landingContext";
import Popup from "./Popup";
import { useTranslation } from "react-i18next";
import { popupTriggers } from "../data/popupConditions";
import { useParams } from "react-router-dom";

export default function Quiz({ onProgressChange }) {
    const { t } = useTranslation();
    const { lng } = useParams();
    const lang = lng === "ar" ? "ar" : "he";

    const [currentNodeId, setCurrentNodeId] = useState("q_1a");
    const [node, setNode] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [history, setHistory] = useState(["q_1a"]); // ✅ עקבי עם תחילת הקוויז
    const [selectedAnswer, setSelectedAnswer] = useState(null);

    const sessionId = useRef(uuidv4()).current;
    const sourceLogged = useRef(false);

    // Log campaign source once per session — fire-and-forget, never blocks the quiz
    useEffect(() => {
        if (sourceLogged.current) return;
        sourceLogged.current = true;

        try {
            supabase
                .from("session_sources")
                .insert({ session_id: sessionId, ...getLandingContext() })
                .then(({ error }) => {
                    if (error) console.error("Error logging session source:", error);
                });
        } catch (e) {
            console.error("Error logging session source:", e);
        }
    }, [sessionId]);

    const [showPopup, setShowPopup] = useState(false);
    const [activePopup, setActivePopup] = useState(null); // ✅ { content, isHtml? }

    const isResult = node?.type === "result";

    // Load node + answers
    useEffect(() => {
        const loadNode = async () => {
            try {
                const nodeData = await fetchNodeById(currentNodeId);
                setNode(nodeData);

                if (nodeData?.type === "question") {
                    const answerData = await fetchAnswersForNode(currentNodeId);
                    setAnswers(answerData);
                } else {
                    setAnswers([]);
                }

                setSelectedAnswer(null);
            } catch (err) {
                console.error("Error loading quiz node:", err);
            }
        };

        loadNode();
    }, [currentNodeId]);

    // Progress
    useEffect(() => {
        const currentProgress =
            node?.type === "result" ? 100 : ((history.length - 1) / 12) * 100;

        if (onProgressChange) onProgressChange(currentProgress);
    }, [node, history, onProgressChange]);

    // Scroll + Popups
    useEffect(() => {
        const isMobile = window.innerWidth < 640;
        const isFirstScreen = node?.id === "q_1";

        if (isMobile && isFirstScreen) {
            smoothScrollTo(window.scrollY + 50, 800);
        }
        if (isMobile && node?.id === "q_2") {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }

        // ✅ Popup by node id
        if (node?.id && popupTriggers[node.id]) {
            const popupObj = popupTriggers[node.id];

            setTimeout(() => {
                setActivePopup(popupObj);
                setShowPopup(true);
            }, 3000);
            return;
        }

        // ✅ Generic result popup
        if (node?.type === "result" && popupTriggers.result_generic) {
            const popupObj = popupTriggers.result_generic;

            setTimeout(() => {
                // אם לא סימנת isHtml בקובץ popupConditions, פה אנחנו מכריחים HTML
                setActivePopup({ ...popupObj, isHtml: true });
                setShowPopup(true);
            }, 3000);
        }
    }, [node]);

    // Log result nodes
    useEffect(() => {
        const logResultNode = async () => {
            if (node?.type === "result") {
                try {
                    await supabase.from("user_answers").insert({
                        session_id: sessionId,
                        node_id: node.id,
                        created_at: new Date().toISOString(),
                    });
                } catch (error) {
                    console.error("Error logging result node:", error);
                }
            }
        };

        logResultNode();
    }, [node, sessionId]);

    const handleNext = async (answerParam) => {
        const answer = answerParam || selectedAnswer;
        if (!answer) return;

        try {
            await supabase.from("user_answers").insert({
                session_id: sessionId,
                node_id: currentNodeId,
                answer_id: answer.id,
            });
        } catch (e) {
            console.error("Error logging answer:", e);
        }

        setHistory((prev) => [...prev, answer.to_node_id]);
        setCurrentNodeId(answer.to_node_id);
        setSelectedAnswer(null);
    };

    const handleBack = () => {
        if (history.length > 1) {
            const newHistory = [...history];
            newHistory.pop();
            const previous = newHistory[newHistory.length - 1];
            setHistory(newHistory);
            setCurrentNodeId(previous);
        }
    };

    const handleRestart = () => {
        setHistory(["q_1a"]);
        setCurrentNodeId("q_1a");
        setSelectedAnswer(null);
        setShowPopup(false);
        setActivePopup(null);
    };

    return (
        <div className="space-y-6 px-4 sm:px-0 pb-24">
            {/* Screen reader title */}
            <h1
                style={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    padding: 0,
                    margin: "-1px",
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                }}
            >
                {t("quiz.srTitle")}
            </h1>

            <QuizNode
                node={node}
                answers={answers}
                selectedAnswer={selectedAnswer}
                lang={lang}
                onSelect={(answer) => {
                    setSelectedAnswer(answer);
                    handleNext(answer); // ✅ אצלך זה auto-next
                }}
            />

            {node?.id !== "q_1" && (
                <>
                    {!isResult && (
                        <>
                            <div className="fixed bottom-4 left-4">
                                <button
                                    onClick={handleRestart}
                                    aria-label={t("quiz.ariaRestart")}
                                    className="cursor-pointer px-4 py-2 border text-[#904750] border-[#D18D97] shadow-sm hover:bg-[#fbeef0] transition rounded text-sm sm:text-base"
                                >
                                    {t("quiz.restart")}
                                </button>
                            </div>

                            <div className="fixed bottom-4 right-4">
                                <button
                                    onClick={handleBack}
                                    aria-label={t("quiz.ariaBack")}
                                    className="cursor-pointer px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-100 transition rounded text-sm sm:text-base"
                                >
                                    {t("quiz.back")}
                                </button>
                            </div>
                        </>
                    )}

                    {isResult && (
                        <div className="flex justify-center gap-4 mt-8">
                            <button
                                onClick={handleRestart}
                                aria-label={t("quiz.ariaRestartResult")}
                                className="cursor-pointer px-4 py-2 border text-[#904750] border-[#D18D97] shadow-sm hover:bg-[#fbeef0] transition rounded text-sm sm:text-base"
                            >
                                {t("quiz.restart")}
                            </button>

                            <button
                                onClick={handleBack}
                                aria-label={t("quiz.ariaBack")}
                                className="cursor-pointer px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-100 transition rounded text-sm sm:text-base"
                            >
                                {t("quiz.back")}
                            </button>
                        </div>
                    )}
                </>
            )}

            {showPopup && activePopup && (
                <Popup
                    popup={activePopup}
                    onClose={() => {
                        setShowPopup(false);
                        setActivePopup(null);
                    }}
                />
            )}
        </div>
    );
}